/*
 * Shared formatting utilities — single source of truth for the entire UI.
 *
 * Previously duplicated across DashboardView, DeviceDrawer, DiagnosticsPanel,
 * NodeSummaryPanel, and NetworkCanvas.
 *
 * ! file utils/formatters.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

/**
 * Format a byte count into a human-readable string (B / KB / MB / GB).
 * @param {number|null|undefined} b
 * @returns {string}
 */
export const fmtBytes = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

/**
 * Format a number with locale-aware thousand separators.
 * @param {number|null|undefined} n
 * @returns {string}
 */
export const fmtNum = (n) => (n != null ? Number(n).toLocaleString() : "—");

/**
 * Format a byte-rate as a human-readable per-second string.
 * @param {number|null|undefined} r
 * @returns {string}
 */
export const fmtRate = (r) => {
  if (r == null || r === 0) return "0 B/s";
  if (r < 1024) return `${r.toFixed(0)} B/s`;
  if (r < 1048576) return `${(r / 1024).toFixed(1)} KB/s`;
  if (r < 1073741824) return `${(r / 1048576).toFixed(1)} MB/s`;
  return `${(r / 1073741824).toFixed(2)} GB/s`;
};

/**
 * Turn a timestamp into a human-readable "time ago" string.
 * Handles ISO strings, Unix timestamps, and Date objects.
 * @param {string|number|Date|null|undefined} ts
 * @returns {string}
 */
export const ago = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "never";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 0) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

/**
 * Truncate a string to `max` characters and append "…" if needed.
 * @param {string} str
 * @param {number} max
 * @returns {string}
 */
export const truncate = (str, max = 32) => {
  if (!str) return "—";
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
};
